import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { SystemStatusContext } from '../../../shared/contexts/system-status.context';
import { combineLatest, EMPTY, Observable, Subscription, throwError } from 'rxjs';
import { LicenseContext } from '../../../shared/contexts/license.context';
import {
  CucmLicenseCount,
  HealthStatus,
  LicenseInfo,
  State,
  StatusCategory,
  SystemHealthStatus,
} from '../../../shared/models/generated/ziroModels';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroFileUploadConfig } from '../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroFormConfig } from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { GlobalPropertiesContext } from '../../../shared/contexts/global-properties.context';
import { CustomUiOptionsContext } from '../../../shared/contexts/custom-ui-options.context';

interface LicenseStatus {
  title: string;
  content: string;
}

interface AdminLicensePayload {
  licenseFileUpload: File;
}

@Component({
  selector: 'ziro-admin-license',
  templateUrl: './admin-license.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class AdminLicenseComponent extends ZiroFormAbstractDirective<AdminLicensePayload> implements OnInit, OnDestroy {
  private bottomNavService = inject(BottomNavService);
  private toastService = inject(ToastService);
  private systemStatusContext = inject(SystemStatusContext);
  private licenseContext = inject(LicenseContext);
  private customUiOptionsContext = inject(CustomUiOptionsContext);
  protected _ziroFormStateService: ZiroFormStateService;
  private breadcrumbsService = inject(BreadcrumbsService);
  private http = inject(HttpClient);
  private globalPropertiesContext = inject(GlobalPropertiesContext);

  isLoading = true;
  isImporting = false;
  licenseInfo: LicenseInfo;
  hasSubmitted = false;
  licenseStatusCause = '';
  licenseStatusMessage = {} as LicenseStatus;
  licenseHealthStatus: HealthStatus;
  ziroIcons = ZiroIcons;
  state = State;
  cucmLicenseUsage: CucmLicenseCount[] = [];
  systemHealthStatus: SystemHealthStatus;
  isHostedEnabled: boolean;

  private _subscriptions = new Subscription();

  formConfig = {
    fields: {
      licenseFileUpload: {
        label: 'tkey;admin.license.filename.label',
        required: true,
        dataAutomation: 'license-import-file-path-text',
        componentConfig: new ZiroFileUploadConfig({
          acceptedFileExtensions: { acceptedExtensions: '.xml' },
          maxKbSize: 51200,
        }),
      },
    },
    options: {
      columnClasses: {
        label: 'col-lg-2 text-lg-end',
        input: 'col-lg-10',
      },
    },
  } as ZiroFormConfig;

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.license.header' }]);
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'importLicenseUpdate',
          dataAutomation: 'import-license-update',
          label: 'tkey;pages.admin.import.bottom_nav.import.label',
          icon: this.ziroIcons.IMPORT,
          buttonClass: ButtonStyles.PRIMARY,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );

    const subscription = combineLatest([
      this.systemStatusContext.state$,
      this.licenseContext.licenseState$,
      this.globalPropertiesContext.state$,
    ]).subscribe(([systemStatus, licenseInfo, globalPropertyInfo]) => {
      if (systemStatus) {
        this.systemHealthStatus = systemStatus;
        this._setErrorMessage(systemStatus);
      }

      this.isHostedEnabled = globalPropertyInfo.hostedEnabled;
      this.licenseInfo = licenseInfo;
      this.licenseHealthStatus = this.systemHealthStatus.healthStatuses.find(
        (healthStatus: HealthStatus) => healthStatus.category === StatusCategory.LICENSES,
      );

      this.cucmLicenseUsage = licenseInfo?.consumedCucmLicenseCountBreakdown
        ? licenseInfo?.consumedCucmLicenseCountBreakdown
        : [];
      this.isLoading = false;
      if (this.hasSubmitted) {
        this.hasSubmitted = false;
        this.isImporting = false;
        this._setPending(false);
        this._validateAndSubmitSource.next(false);
        this._ziroFormStateService.setIsFormDirty(false);
        this.toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.DOWNLOAD,
          'tkey;shared.toast.save.success.title',
          'tkey;admin.license.toast.success.message',
        );
      }
    });
    this._subscriptions.add(subscription);

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscriptions.add(formSubmittedSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  trackByName = (i: number, ciscoUsage: CucmLicenseCount): string => {
    return ciscoUsage.cucmDescription;
  };

  protected submit(): Observable<void> {
    return this._onImportClicked();
  }

  private _setPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'importLicenseUpdate',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );

    this.isImporting = setting;
  }

  private _onImportClicked(): Observable<void> {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: !this.formData.licenseFileUpload,
      }),
    );

    this.hasSubmitted = true;

    if (this.formData.licenseFileUpload) {
      this.isImporting = true;

      this._setPending(true);

      return this._postLicenseFile(this.formData.licenseFileUpload).pipe(
        tap(),
        catchError((response) => {
          this.isImporting = false;
          this._setPending(false);
          if (response?.error?.description === 'License is not valid') {
            this.toastService.push(
              ToastTypes.ERROR,
              this.ziroIcons.DOWNLOAD,
              'tkey;shared.toast.save.fail.title',
              'tkey;admin.license.toast.invalid.message',
            );
            return throwError(() => EMPTY);
          } else {
            return throwError(() => response);
          }
        }),
      );
    }
  }

  private _postLicenseFile(file: File): Observable<void> {
    const formData = new FormData();
    formData.append('upload', file);
    return this.http.post('/services/cisco/admin/licenses/file', formData).pipe(
      mergeMap(() => this.systemStatusContext.refreshSystemStatus()),
      mergeMap(() => this.licenseContext.refreshLicenseInfo()),
      mergeMap(() => this.customUiOptionsContext.refresh()),
    );
  }

  private _setErrorMessage(systemStatusAlertState: SystemHealthStatus): void {
    this.licenseStatusCause = systemStatusAlertState.healthStatuses.find(
      (status) => status.category === StatusCategory.LICENSES,
    )?.cause;
    switch (this.licenseStatusCause) {
      case 'No license installed': {
        this.licenseStatusMessage = {
          title: 'tkey;admin.license.missing_license.title',
          content: 'tkey;admin.license.missing_license.text',
        };
        break;
      }
      case 'License is expired': {
        this.licenseStatusMessage = {
          title: 'tkey;admin.license.expired_license.title',
          content: 'tkey;admin.license.expired_license.text',
        };
        break;
      }
      case 'Exceeded number of licenses': {
        this.licenseStatusMessage = {
          title: 'tkey;admin.license.overused_license.title',
          content: 'tkey;admin.license.overused_license.text',
        };
        break;
      }
      default: {
        this.licenseStatusMessage = {
          title: '',
          content: '',
        };
      }
    }
  }
}
